import React, {useEffect, useState, useCallback} from 'react';
import {useDispatch} from 'react-redux';
import {Menu} from '../../utils/menu/Menu';
import {materialError} from './utils/materialError';
import {toast} from 'react-toastify';
import {SortItem} from '../../utils/sortItem/SortItem';
import {ButtonMore} from '../../utils/buttonMore/buttonMore';
import {ConfirmModal} from '../../utils/confirm/ConfirmModal';
import {
  materialsAction,
  materialStagesAction,
  materialStageDeleteAction,
  materialDeleteAction,
  materialFileDeleteAction,
  materialStagePresetsAction,
  materialStagesFromCourseAction,
} from '../../actions/materialAction';
import {StageModal} from './utils/StageModal';
import {MaterialModal} from './utils/MaterialModal';
import {UploadModal} from './utils/UploadModal';

/**
 * Материалы маршрута — то, что клиентка видит в приложении на вкладке «Материалы».
 *
 * Экран построен как сам маршрут: ступени, внутри каждой её материалы. Так же
 * его видит клиентка, и наполняющему не приходится держать соответствие в голове.
 *
 * Ступень клиентки вычисляет сервер по дням от старта программы — здесь её никто
 * не переключает. Пустой маршрут для приложения не поломка: оно скажет
 * «Материалов пока нет», и это правда до тех пор, пока их не выложили.
 */
const Materials = () => {
  const dispatch = useDispatch();
  const [stages, setStages] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [opened, setOpened] = useState({});
  const [presets, setPresets] = useState([]);
  const [loading, setLoading] = useState(false);

  const getList = useCallback(() => {
    Promise.all([
      dispatch(materialStagesAction()),
      dispatch(materialsAction()),
      dispatch(materialStagePresetsAction()),
    ]).then(([stagesData, materialsData, presetsData]) => {
      setStages(Array.isArray(stagesData) ? stagesData : []);
      setMaterials(Array.isArray(materialsData) ? materialsData : []);
      setPresets(Array.isArray(presetsData) ? presetsData : []);
    }).catch(err => {
      toast.error(materialError(err), {position: toast.POSITION.BOTTOM_RIGHT});
    });
  }, [dispatch]);

  useEffect(() => {
    getList();
  }, [getList]);

  // Одно действие на все удаления: разница только в ручке, а поведение общее —
  // не даём нажать второй раз, пока идёт первое, и перечитываем список
  const handleDelete = (action) => {
    return new Promise((resolve, reject) => {
      if (loading) {
        return resolve(false);
      }
      setLoading(true);
      dispatch(action).then(() => {
        setLoading(false);
        getList();
        resolve(true);
      }).catch(err => {
        setLoading(false);
        toast.error(materialError(err), {position: toast.POSITION.BOTTOM_RIGHT});
        // Список перечитывается и на отказе: если строки уже нет, оставить её на
        // экране хуже самой ошибки — по ней нажмут снова и зальют файл в никуда
        getList();
        reject(err);
      });
    });
  };

  const stageMaterials = (stageId) => materials.filter((m) => m.stage_id === stageId);

  // Ступени курса уже описаны в CRM: пока какие-то не заведены, предлагаем
  // сделать это одним нажатием — набирать вручную то, что система и так знает,
  // значит однажды ошибиться в дне и разойтись с расчётом ступени на сервере
  const missing = presets.filter((p) => !p.exists);

  const handleFromCourse = () => {
    if (loading) {
      return;
    }
    setLoading(true);
    dispatch(materialStagesFromCourseAction()).then((res) => {
      setLoading(false);
      getList();
      const created = res && res.created ? res.created : 0;
      toast.success('Заведено ступеней: ' + created, {position: toast.POSITION.BOTTOM_RIGHT});
    }).catch(err => {
      setLoading(false);
      toast.error(materialError(err), {position: toast.POSITION.BOTTOM_RIGHT});
    });
  };

  return <div className="content">
    <Menu isStatic={true}/>

    <div className="heading heading--flex">
      <h2 className="heading__title">Материалы</h2>
      <StageModal presets={presets} onSuccess={() => getList()} triggerWrap={true}
                  trigger={<button type="button" className="heading__btn heading__btn--invite btn modal-btn">+ Ступень</button>}/>
      {missing.length > 0 && <button type="button" className="heading__btn btn modal-btn" style={{marginLeft: '10px'}}
                                     onClick={handleFromCourse} disabled={loading}>
        Завести из курса ({missing.length})
      </button>}
    </div>

    <div className="users">
      <div className="users__wrapper">
        <div className="sort">
          <SortItem title="Ступень" className="sort__item--name"/>
          <SortItem title="Дни и недели" className="sort__item--email"/>
        </div>
        <div className="users__inner">
          {stages.map((stage) => <React.Fragment key={stage.id}>
            {/* Ступень — заголовок группы, а не такая же строка, как материал:
                иначе список читается сплошной лентой, в которой не видно, где
                кончается одна ступень и начинается другая */}
            <div className="users__item" style={{padding: '14px 0', marginTop: '10px'}}>
              <span className="users__name" style={{cursor: 'pointer', display: 'block'}}
                    onClick={() => setOpened((value) => ({...value, [stage.id]: !value[stage.id]}))}>
                <span style={{display: 'flex', alignItems: 'center', gap: '8px'}}>
                  <span style={{opacity: 0.5, width: '10px'}}>{opened[stage.id] ? '▾' : '▸'}</span>
                  <b>{stage.order}. {stage.title}</b>
                </span>
                {/* Второй строкой, а не в хвосте названия: счёт материалов и имя
                    ступени — про неё саму, но читаются они реже, чем название */}
                <span className="help" style={{display: 'block', marginTop: '4px', paddingLeft: '18px'}}>
                  {stageMaterials(stage.id).length} материалов
                  {stage.uname ? ' · ' + stage.uname : ''}
                </span>
              </span>
              {/* Дни — то, чем ступень задана и правится; недели рядом, потому что
                  именно ими о сроке говорят с клиенткой */}
              <span className="users__email">
                {stage.day_from}–{stage.day_to} день · {stage.week_from}–{stage.week_to} неделя
              </span>
              <ButtonMore>
                <ul className="dropdown">
                  <li>
                    <MaterialModal stageId={stage.id} triggerWrap={false} onSuccess={() => getList()}
                                   trigger={<div className="dropdown__item icon-mark">Добавить материал</div>}/>
                  </li>
                  <li>
                    <StageModal item={stage} presets={presets} triggerWrap={false} onSuccess={() => getList()}
                                trigger={<div className="dropdown__item icon-edit">Поправить ступень</div>}/>
                  </li>
                  <li>
                    <ConfirmModal trigger={<div className="dropdown__item icon-delete">Убрать ступень</div>}
                                  triggerWrap={false}
                                  title={"Убрать ступень вместе с материалами? У клиенток она пропадёт из маршрута."}
                                  handleOk={() => handleDelete(materialStageDeleteAction(stage.id))}/>
                  </li>
                </ul>
              </ButtonMore>
            </div>

            {/* Пустая ступень — обычное дело: её заводят до того, как выложат
                материалы. Молчаливый пробел на этом месте читается как поломка */}
            {opened[stage.id] && stageMaterials(stage.id).length === 0 && <div className="users__item"
                                                                               style={{padding: '10px 0 10px 40px'}}>
              <span className="help">Материалов пока нет</span>
            </div>}

            {opened[stage.id] && stageMaterials(stage.id).map((material) => <div key={material.id}
                                                                                className="users__item"
                                                                                style={{padding: '12px 0 12px 40px'}}>
              <span className="users__name" style={{display: 'block'}}>
                {material.title}
                <span className="help" style={{display: 'flex', alignItems: 'center', gap: '10px',
                                               flexWrap: 'wrap', marginTop: '4px'}}>
                  <span>{material.kind === 'video' ? 'видеозапись' : 'пособие'}</span>
                  {/* Файла нет — материал в приложении не скачается и не заиграет:
                      сказать об этом надо здесь, а не по жалобе клиентки.
                      Считаем по признаку сервера: у залитого к нам материала адрес
                      в базе пуст, он собирается подписью при чтении манифеста */}
                  {!material.has_file && <span className="user-info__status user-info__status--block">без файла</span>}
                  {/* Плейсхолдер для сообщения: куратор вставляет его в текст, и в
                      приложении он разворачивается в карточку. Моноширинным и
                      выделяемым целиком: его копируют, а не читают */}
                  {material.placeholder && <span style={{fontFamily: 'monospace', userSelect: 'all'}}
                                                 title="Вставьте это в сообщение — клиентке уйдёт карточка материала">
                    {material.placeholder}
                  </span>}
                </span>
              </span>
              {/* Варианты пособия — во второй колонке: это про сам материал,
                  но читают их реже, чем название */}
              <span className="users__email">
                {material.files && material.files.length > 0
                  ? material.files.map((f) => f.title).join(', ')
                  : ''}
              </span>
              <ButtonMore>
                <ul className="dropdown">
                  <li>
                    <UploadModal material={material} triggerWrap={false} onSuccess={() => getList()}
                                 trigger={<div className="dropdown__item icon-mark">Залить файл</div>}/>
                  </li>
                  <li>
                    <MaterialModal item={material} triggerWrap={false} onSuccess={() => getList()}
                                   trigger={<div className="dropdown__item icon-edit">Поправить</div>}/>
                  </li>
                  {material.files && material.files.map((file) => <ConfirmModal key={file.id} triggerWrap={false}
                    trigger={<div className="dropdown__item icon-delete">Снять «{file.title}»</div>}
                    title={"Снять файл «" + file.title + "»? Он будет удалён из хранилища."}
                    handleOk={() => handleDelete(materialFileDeleteAction(file.id))}/>)}
                  <li>
                    <ConfirmModal trigger={<div className="dropdown__item icon-delete">Убрать материал</div>}
                                  triggerWrap={false}
                                  title={"Убрать материал? У клиенток он пропадёт из ступени."}
                                  handleOk={() => handleDelete(materialDeleteAction(material.id))}/>
                  </li>
                </ul>
              </ButtonMore>
            </div>)}

            {opened[stage.id] && stageMaterials(stage.id).length === 0 && <div className="users__item"
                                                                              style={{paddingLeft: '32px'}}>
              <span className="users__name">В этой ступени материалов пока нет</span>
            </div>}
          </React.Fragment>)}

          {stages.length === 0 && <div className="users__item">
            <span className="users__name">
              Ступеней пока нет — маршрут у клиенток будет пустым.
              {missing.length > 0 && ' Их можно завести из описания курса кнопкой сверху.'}
            </span>
          </div>}
        </div>
      </div>
    </div>
  </div>;
};

export default Materials;
